import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiRequestError, apiRequest, setAccessToken } from '../lib/api-client';
import { messageFor } from '../lib/errors';
import { t } from '../lib/i18n';
import { AuthLayout } from './AuthLayout';
import { useAuth } from './auth-store';
import { ArrowIcon, EyeIcon, LockIcon, UserIcon } from './icons';

/**
 * Accepting an invitation.
 *
 * It ends in a session rather than at the sign-in screen: somebody who just opened a link sent to
 * their address has proved who they are, and asking them to go and sign in with a password they may
 * not have yet is asking them to fail.
 *
 * The name and password are shown for everybody because the page cannot know, before it tries,
 * whether the address already has an account — and asking the server first would tell whoever holds
 * the link whether that address is registered. Somebody who already has an account may leave both
 * blank; the server ignores them.
 */

const MESSAGES: Record<string, string> = {
  'access.invalid_invitation': 'invite.invalid',
  'access.invitation_used': 'invite.used',
  'access.invitation_expired': 'invite.expired',
};

interface AcceptResponse {
  access_token: string;
  user: { name: string };
}

export function InvitePage(): React.JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const token = params.get('token') ?? '';

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setReasons([]);

    try {
      const result = await apiRequest<AcceptResponse>('/v1/auth/invitations/accept', {
        method: 'POST',
        body: {
          token,
          ...(name.trim() ? { name: name.trim() } : {}),
          ...(password ? { password } : {}),
        },
      });

      setAccessToken(result.access_token);
      await refreshUser();

      // `replace`, so the token is not in the history entry the back button returns to.
      navigate('/', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.code === 'auth.weak_password') {
        const details = caught.payload?.details as { reasons?: string[] } | undefined;

        setReasons(details?.reasons ?? []);
        setError(t('signup.weakPassword'));
        return;
      }

      const key = caught instanceof ApiRequestError ? MESSAGES[caught.code] : undefined;

      setError(key ? t(key) : messageFor(caught));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout>
        <div className="auth__done">
          <h1>{t('invite.title')}</h1>
          <p>{t('invite.missingToken')}</p>

          <Link className="button button--primary" to="/entrar">
            {t('forgot.backToLogin')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <header className="auth__heading">
        <h1>{t('invite.title')}</h1>
        <p>{t('invite.subtitle')}</p>
      </header>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className="input">
          <label htmlFor="invite-name">{t('field.name')}</label>
          <div className="input__control">
            <UserIcon />
            <input
              id="invite-name"
              name="name"
              autoComplete="name"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        </div>

        <div className="input">
          <label htmlFor="invite-password">{t('field.password')}</label>
          <div className="input__control">
            <LockIcon />
            <input
              id="invite-password"
              type={revealed ? 'text' : 'password'}
              name="new-password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              className="input__reveal"
              onClick={() => setRevealed((current) => !current)}
              aria-label={revealed ? t('login.hidePassword') : t('login.showPassword')}
              aria-pressed={revealed}
            >
              <EyeIcon crossed={revealed} />
            </button>
          </div>
          <p className="input__hint">{t('invite.passwordHint')}</p>
        </div>

        {error && (
          <div className="alert" role="alert">
            <p>{error}</p>
            {reasons.length > 0 && (
              <ul>
                {reasons.map((reason) => (
                  <li key={reason}>{t(`password.reason.${reason}`)}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button type="submit" className="button button--primary" disabled={submitting}>
          {submitting ? t('invite.joining') : t('invite.join')}
          {!submitting && <ArrowIcon />}
        </button>
      </form>

      <p className="auth__switch">
        <Link to="/entrar">{t('forgot.backToLogin')}</Link>
      </p>
    </AuthLayout>
  );
}
